import { Component, Injector, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/common/base/base.component';
import {
  WFNode,
  WorkflowTreeComponent,
} from './workflow-tree/workflow-tree.component';
import { WFStepConfig, WFTableConfig, WFTableResource } from './workflow';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { ComponentType, Roles } from 'src/app/common/enumerations/enumerations';
import { WorkflowMainComponent } from './workflow-main/workflow-main.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';

@Component({
  selector: 'app-workflow',
  templateUrl: './workflow.component.html',
  styleUrls: ['./workflow.component.scss'],
})
/**
 * The `WorkflowComponent` is responsible for managing and displaying workflow-related data and actions
 * within the application. It extends the `BaseComponent` and interacts with workflow tables, nodes,
 * and configurations. The component provides functionality for filtering, displaying workflow information,
 * creating, modifying, duplicating, saving, and approving workflow table configurations.
 *
 * ## Features
 * - Displays workflow information and details.
 * - Applies filters to workflow data.
 * - Loads and manages workflow table configurations.
 * - Supports creating, editing, duplicating, and saving workflow configurations.
 * - Handles approval logic based on user roles and workflow status.
 * - Integrates with child components for workflow tree and main workflow views.
 *
 * ## Dependencies
 * - `WorkFlowService`: Service for interacting with workflow-related backend APIs.
 * - `WorkflowTreeComponent` and `WorkflowMainComponent`: Child components for workflow visualization and editing.
 *
 * @extends BaseComponent
 */
export class WorkflowComponent extends BaseComponent {
  valueFilter!: string;
  filter?: any;

  wfTableConfig?: WFTableConfig;
  wfTables?: WFTableResource[];
  node?: WFNode;

  canApprove?: boolean;

  editing?: boolean;
  creating?: boolean;

  showLines?: boolean;

  @ViewChild(WorkflowTreeComponent) wfTree?: WorkflowTreeComponent;
  @ViewChild(WorkflowMainComponent) wfMain?: WorkflowMainComponent;

  constructor(
    protected override injector: Injector,
    public service: WorkFlowService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.moduleTitle = 'Global Workflows';
  }

  applyFilter(e: any) {
    if (!e) this.valueFilter = '';
    this.filter = e;
  }

  displayInfo(e?: WFNode) {
    this.node = e;
    if (!e) this.wfTableConfig = undefined;
    this.service.loading = true;
    if (e?.id && this.wfTableConfig?.id != e?.tableId) {
      this.getData(e);
    } else {
      // this.scrollTo(e);
      this.service.loading = false;
    }
  }

  getData(node?: WFNode) {
    this.service.loading = true;
    this.service.getWFTableConfigByID(node!.tableId).subscribe((data) => {
      this.wfTableConfig = data;
      this.wfTableConfig.approvedByName = this.users.find(
        (u) => u.id == this.wfTableConfig?.approvedBy
      )?.name;
      this.wfTableConfig.createdByName = this.users.find(
        (u) => u.id == this.wfTableConfig?.createdBy
      )?.name;
      this.wfTableConfig.updatedByName = this.users.find(
        (u) => u.id == this.wfTableConfig?.updatedBy
      )?.name;
      this.canApprove =
        this.hasPrivilege(this.privilegeEnum.GlobalWorkflowApprover) &&
        this.wfTableConfig.createdBy != this.currentUser?.id;
      this.service.loading = false;
    });
  }

  scrollTo(node?: WFNode) {
    if (node?.sectionId) {
      const element = document.getElementById(`section-${node.sectionId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /**
   * Saves the current workflow table configuration.
   *
   * If the configuration (`wfTableConfig`) has an `id`, it updates the existing configuration
   * by calling the service's `putWFTableConfig` method. Otherwise, it creates a new configuration
   * using the `postWFTableConfig` method.
   *
   * After a successful save or update, it refreshes the workflow tree for the current node
   * and updates the editing/creating state flags accordingly.
   */
  save() {
    if (this.wfTableConfig) {
      this.checkEndSteps();
      if (this.wfTableConfig?.id) {
        this.service.putWFTableConfig(this.wfTableConfig).subscribe(
          (data) => {
            this.getData(this.node);
            this.wfTableConfig = data;
            this.wfTree?.refresh(this.node!);
            setTimeout(() => {
              this.wfMain?.initArrows();
            }, 1000);
          },
          (error) => {
            console.log(error);
            this.alert.defaultError();
          }
        );
      } else {
        this.service.postWFTableConfig(this.wfTableConfig).subscribe(
          (data) => {
            this.wfTableConfig = data;
            this.wfTree?.refresh(this.node!);
            setTimeout(() => {
              this.wfMain?.initArrows();
            }, 1000);
            this.creating = false;
          },
          (error) => {
            console.log(error);
            this.alert.defaultError();
          }
        );
      }
    }
  }

  checkEndSteps() {
    const steps = this.wfTableConfig?.wfSectionConfigs?.flatMap(
      (s) => s.wfStepConfigs
    );
    steps?.forEach((s) => {
      this.checkEndStep(s);
    });
  }

  checkEndStep(step: WFStepConfig) {
    step.end =
      step.wfTaskConfigs?.some((t) => t.type == ComponentType.CloseButton) ??
      false;
  }
  /**
   * Opens a confirmation dialog to approve the current workflow table configuration.
   * If the user confirms, calls the service to approve the workflow table configuration,
   * updates the local configuration with the response, and refreshes the workflow tree node.
   *
   * @remarks
   * - Displays a warning message in the confirmation dialog.
   * - Only executes if `wfTableConfig` is defined.
   */
  approve() {
    if (this.wfTableConfig) {
      const dialog = this.dialog.open(YesNoDialogComponent, {
        width: '500px',
        data: {
          message:
            'Are you sure you want to approve this Workflow configuration?',
          icon: 'warn',
        },
      });

      dialog.afterClosed().subscribe(async (confirmed) => {
        if (confirmed) {
          this.service
            .approveWFTableConfig(this.wfTableConfig!.id)
            .subscribe((data) => {
              this.wfTableConfig = data;
              this.alert.success(
                'Workflow configuration approved successfully.'
              );
              this.wfTree?.refresh(this.node!);
              setTimeout(() => {
                this.wfMain?.initArrows();
              }, 1000);
            });
        }
      });
    }
  }

  create() {
    this.wfTableConfig = new WFTableConfig();
    this.wfTableConfig.status = -1;
    this.creating = true;
  }

  modify() {
    // this.wfMain?.edit();
    this.editing = true;
  }

  duplicate() {
    if (this.wfTableConfig?.id)
      this.service
        .duplicateWFTableConfig(this.wfTableConfig.id)
        .subscribe((data) => {
          this.wfTableConfig = data;
          this.getData(this.node);
          this.wfTree?.rebuildTree(this.wfTableConfig.id);
        });
  }

  delete() {
    if (this.wfTableConfig?.id)
      this.service.deleteWFTableConfig(this.wfTableConfig).subscribe((data) => {
        this.wfTree?.rebuildTree();
        this.wfTableConfig = undefined;
      });
  }

  cancel() {
    this.editing = false;
    this.creating = false;
  }
}
